import { Injectable, inject } from '@angular/core';
import interact from 'interactjs';
import { assert } from 'src/app/core/assert/assert';
import {
  PosYAttribute,
  SortableRowItemIdAttribute,
  SortedIndexAttribute,
} from '../../../constants-repository/sortable-rows.constants';
import { SortableRowsDirective } from '../../../directives/sortable-rows.directive';
import { ShiveAnimationsService } from '../../rendering/shive-animations.service';
import { ShiveRenderService } from '../../rendering/shive-render.service';
import { SortableEntriesDefaultAnimationDuration } from './sortable-elements.constants';

@Injectable({
  providedIn: 'root',
})
export class SortableEntriesService<T extends { id: number }> {
  private readonly shiveRenderService = inject(ShiveRenderService);
  private readonly shiveAnimationService = inject(ShiveAnimationsService);
  private readonly htmlTableRowItems: HTMLTableRowElement[] = [];
  private dragBtnClicked = false;

  public initializeDragBehavior(invoicePositions: readonly SortableRowsDirective<T>[]): void {
    invoicePositions.forEach((invoicePosition) => {
      const htmlTableRowItem = invoicePosition.el.nativeElement;
      this.htmlTableRowItems.push(htmlTableRowItem);

      interact(htmlTableRowItem).on('down', (event) => {
        if (!this.dragBtnClicked) {
          return;
        }

        event.interaction.start({ name: 'drag' }, event.interactable, htmlTableRowItem);
        this.dragBtnClicked = false;
      });
    });
  }

  public dispatchMouseDownEvent(sortableRow: T): void {
    const tableRow = this.htmlTableRowItems.find((tableRow) => {
      const id = Number(this.shiveRenderService.getAttribute(tableRow, SortableRowItemIdAttribute));
      return sortableRow.id === id;
    })!;
    tableRow.dispatchEvent(new Event('mousedown'));
    this.dragBtnClicked = true;
  }

  public updateAndAnimateEntries(
    entry: HTMLElement,
    index: number,
    deltaY: number,
    reset?: boolean,
    animationDuration = SortableEntriesDefaultAnimationDuration,
  ): void {
    this.setSortedIndex(entry, index);
    this.shiftY(entry, deltaY, reset);

    this.shiveAnimationService.triggerSortableRowsNew(
      entry,
      this.getY(entry),
      animationDuration,
      reset,
    );
  }

  public getSortedIndex(entry: HTMLElement): number {
    return Number(this.shiveRenderService.getAttribute(entry, SortedIndexAttribute));
  }

  public setSortedIndex(entry: HTMLElement, idx: number): void {
    this.shiveRenderService.setAttribute(entry, SortedIndexAttribute, String(idx));
  }

  public getY(entry: HTMLElement): number {
    return Number(this.shiveRenderService.getAttribute(entry, PosYAttribute));
  }

  public setY(entry: HTMLElement, y: number): void {
    this.shiveRenderService.setAttribute(entry, PosYAttribute, String(y));
  }

  /**
   * Shift a row’s Y attribute by delta, keeping it in sync with its transform.
   */
  public shiftY(entry: HTMLElement, delta: number, reset?: boolean): void {
    const updated = reset ? 0 : this.getY(entry) + delta;
    this.setY(entry, updated);
  }

  public moveElementUpAndDown(
    target: HTMLElement,
    container: HTMLElement,
    curSortedIdx: number,
    cssSelector: string,
  ): number | null {
    const elementHeight = target.getBoundingClientRect().height;

    const targetSortedIndex = this.getSortedIndex(target);
    const delta = curSortedIdx - targetSortedIndex; // how many rows to move.

    if (Math.abs(delta) === 1) {
      // Move one row.
      this.updateAndAnimateEntries(target, targetSortedIndex + delta, delta * elementHeight);
    } else {
      // Move more than one row.
      let index = curSortedIdx;

      for (let i = 0; i < Math.abs(delta); i++) {
        const direction = delta > 0 ? -1 : 1;
        index += direction;
        const row = container.querySelector(
          `${cssSelector}[${SortedIndexAttribute}="${index}"]`,
        ) as HTMLElement;
        assert(row !== null);
        this.updateAndAnimateEntries(row, index - direction, -direction * elementHeight);
      }
    }

    return delta;
  }
}
